'use client'

import type React from 'react'
import { useState, useEffect, useRef, useCallback } from 'react'
import type { Table as TanStackTable } from '@tanstack/react-table'

type Coord = { rowIndex: number; colIndex: number }
type CellRef = { id: string; colField: string }
type Box = { x: number; y: number; width: number; height: number }
type Options = { onSelectionChange?: (cells: CellRef[]) => void }
type EdgeState = { top: boolean; bottom: boolean; left: boolean; right: boolean }
type EdgeIntensity = { top: number; bottom: number; left: number; right: number }

const EDGE_ZONE_DESKTOP = 50
const EDGE_ZONE_MOBILE = 80
const BASE_SCROLL_SPEED = 4
const MAX_SCROLL_SPEED = 20

const isTouchDevice = () => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
const getEdgeZone = () => isTouchDevice() ? EDGE_ZONE_MOBILE : EDGE_ZONE_DESKTOP

export default function useCellSelection(
  containerRef: React.RefObject<HTMLElement>,
  getCellsInfo: () => { id: string; colField: string; x: number; y: number; width: number; height: number }[],
  _data: any[],
  _columnsDef: any[],
  table: TanStackTable<any>,
  options: Options = {},
) {
  const [selectionBox, setSelectionBox] = useState<Box | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedCells, setSelectedCells] = useState<CellRef[]>([])
  const [anchorCell, setAnchorCell] = useState<Coord | null>(null)
  const [focusCell, setFocusCell] = useState<Coord | null>(null)
  const [edgeState, setEdgeState] = useState<EdgeState>({ top: false, bottom: false, left: false, right: false })
  const [edgeIntensity, setEdgeIntensity] = useState<EdgeIntensity>({ top: 0, bottom: 0, left: 0, right: 0 })

  const startPointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const draggingRef = useRef(false)
  const rafIdRef = useRef<number | null>(null)
  const scrollIntervalRef = useRef<number | null>(null)
  const lastMousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const currentIntensityRef = useRef<EdgeIntensity>({ top: 0, bottom: 0, left: 0, right: 0 })

  const rows = table?.getRowModel()?.rows || []
  const colCount = table?.getVisibleFlatColumns()?.length || 0
  const { onSelectionChange } = options

  const isEmpty = (val: any) => val === null || val === undefined || val === ''

  const areSelectionsEqual = (arrA: CellRef[], arrB: CellRef[]) => {
    if (arrA.length !== arrB.length) return false
    const a = [...arrA].sort((x, y) => (x.id + x.colField).localeCompare(y.id + y.colField))
    const b = [...arrB].sort((x, y) => (x.id + x.colField).localeCompare(y.id + y.colField))
    for (let i = 0; i < a.length; i++) {
      if (a[i].id !== b[i].id || a[i].colField !== b[i].colField) return false
    }
    return true
  }

  const rectsIntersect = useCallback((r1: Box, cell: { x: number; y: number; width: number; height: number }) => {
    const r2 = { x: cell.x, y: cell.y, width: cell.width, height: cell.height }
    return !(r2.x > r1.x + r1.width || r2.x + r2.width < r1.x || r2.y > r1.y + r1.height || r2.y + r2.height < r1.y)
  }, [])

  const getCellValue = useCallback((rowIndex: number, colIndex: number) => {
    const rowObj = rows[rowIndex]
    const colObj = table.getVisibleFlatColumns()[colIndex]
    return rowObj?.original?.[colObj?.id]
  }, [rows, table])

  const getCellsInRange = useCallback((start: Coord, end: Coord) => {
    const rowStart = Math.min(start.rowIndex, end.rowIndex)
    const rowEnd = Math.max(start.rowIndex, end.rowIndex)
    const colStart = Math.min(start.colIndex, end.colIndex)
    const colEnd = Math.max(start.colIndex, end.colIndex)
    const out: CellRef[] = []
    for (let r = rowStart; r <= rowEnd; r++) {
      const rowId = rows[r]?.id
      if (rowId == null) continue
      for (let c = colStart; c <= colEnd; c++) {
        const colId = table.getVisibleFlatColumns()[c]?.id
        if (!colId) continue
        out.push({ id: String(rowId), colField: colId })
      }
    }
    return out
  }, [rows, table])

  const updateSelection = useCallback((newSelected: CellRef[]) => {
    setSelectedCells(prev => {
      if (!areSelectionsEqual(prev, newSelected)) onSelectionChange?.(newSelected)
      return newSelected
    })
  }, [onSelectionChange])

  const detectClickedCell = useCallback((clientX: number, clientY: number): Coord | null => {
    const container = containerRef.current
    if (!container) return null
    const cells = container.querySelectorAll('[data-rowindex][data-colindex]')
    for (const el of Array.from(cells)) {
      const rect = (el as HTMLElement).getBoundingClientRect()
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        const r = Number.parseInt((el as HTMLElement).getAttribute('data-rowindex') || '', 10)
        const c = Number.parseInt((el as HTMLElement).getAttribute('data-colindex') || '', 10)
        if (!Number.isNaN(r) && !Number.isNaN(c)) return { rowIndex: r, colIndex: c }
      }
    }
    return null
  }, [containerRef])

  const handleSingleClickSelection = useCallback((clickedCellPos: Coord) => {
    draggingRef.current = true
    setIsSelecting(true)
    document.body.style.userSelect = 'none'
    setAnchorCell(clickedCellPos)
    setFocusCell(clickedCellPos)

    const rowId = rows[clickedCellPos.rowIndex]?.id
    const colField = table.getVisibleFlatColumns()[clickedCellPos.colIndex]?.id
    if (rowId != null && colField) updateSelection([{ id: String(rowId), colField }])
  }, [rows, table, updateSelection])

  const checkEdgeProximity = useCallback((clientX: number, clientY: number): { edges: EdgeState; intensity: EdgeIntensity } => {
    const container = containerRef.current
    const edgeZone = getEdgeZone()
    if (!container) return {
      edges: { top: false, bottom: false, left: false, right: false },
      intensity: { top: 0, bottom: 0, left: 0, right: 0 }
    }
    const rect = container.getBoundingClientRect()

    const calcIntensity = (distance: number) => Math.max(0, Math.min(1, 1 - (distance / edgeZone)))

    const topDist = clientY - rect.top
    const bottomDist = rect.bottom - clientY
    const leftDist = clientX - rect.left
    const rightDist = rect.right - clientX

    const isAbove = topDist < 0
    const isBelow = bottomDist < 0
    const isLeft = leftDist < 0
    const isRight = rightDist < 0

    return {
      edges: {
        top: isAbove || (topDist < edgeZone && topDist >= 0),
        bottom: isBelow || (bottomDist < edgeZone && bottomDist >= 0),
        left: isLeft || (leftDist < edgeZone && leftDist >= 0),
        right: isRight || (rightDist < edgeZone && rightDist >= 0),
      },
      intensity: {
        top: isAbove ? 1 : (topDist < edgeZone && topDist >= 0 ? calcIntensity(topDist) : 0),
        bottom: isBelow ? 1 : (bottomDist < edgeZone && bottomDist >= 0 ? calcIntensity(bottomDist) : 0),
        left: isLeft ? 1 : (leftDist < edgeZone && leftDist >= 0 ? calcIntensity(leftDist) : 0),
        right: isRight ? 1 : (rightDist < edgeZone && rightDist >= 0 ? calcIntensity(rightDist) : 0),
      }
    }
  }, [containerRef])

  const startAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) return
    scrollIntervalRef.current = window.setInterval(() => {
      const container = containerRef.current
      if (!container) return
      const { top, bottom, left, right } = currentIntensityRef.current
      const getSpeed = (intensity: number) => BASE_SCROLL_SPEED + (intensity * (MAX_SCROLL_SPEED - BASE_SCROLL_SPEED))
      if (top > 0) container.scrollTop -= getSpeed(top)
      if (bottom > 0) container.scrollTop += getSpeed(bottom)
      if (left > 0) container.scrollLeft -= getSpeed(left)
      if (right > 0) container.scrollLeft += getSpeed(right)
    }, 16)
  }, [containerRef])

  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
    setEdgeState({ top: false, bottom: false, left: false, right: false })
    setEdgeIntensity({ top: 0, bottom: 0, left: 0, right: 0 })
    currentIntensityRef.current = { top: 0, bottom: 0, left: 0, right: 0 }
  }, [])

  const handleMouseMoveRaf = useCallback((e: MouseEvent) => {
    const { x: startX, y: startY } = startPointRef.current
    const box: Box = {
      x: Math.min(startX, e.clientX),
      y: Math.min(startY, e.clientY),
      width: Math.abs(e.clientX - startX),
      height: Math.abs(e.clientY - startY),
    }
    setSelectionBox(box)
    const allCells = getCellsInfo()
    updateSelection(allCells.filter(cell => rectsIntersect(box, cell)))

    lastMousePosRef.current = { x: e.clientX, y: e.clientY }
    const { edges, intensity } = checkEdgeProximity(e.clientX, e.clientY)
    setEdgeState(edges)
    setEdgeIntensity(intensity)
    currentIntensityRef.current = intensity
    if (edges.top || edges.bottom || edges.left || edges.right) {
      startAutoScroll()
    } else {
      stopAutoScroll()
    }
  }, [getCellsInfo, rectsIntersect, updateSelection, checkEdgeProximity, startAutoScroll, stopAutoScroll])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isSelecting || !draggingRef.current) return
    e.preventDefault()
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    rafIdRef.current = requestAnimationFrame(() => handleMouseMoveRaf(e))
  }, [handleMouseMoveRaf, isSelecting])

  const finishDrag = useCallback(() => {
    if (isSelecting && selectionBox) {
      const allCells = getCellsInfo()
      updateSelection(allCells.filter(cell => rectsIntersect(selectionBox, cell)))
    }
    setIsSelecting(false)
    draggingRef.current = false
    startPointRef.current = { x: 0, y: 0 }
    setSelectionBox(null)
    document.body.style.userSelect = ''
    stopAutoScroll()
  }, [isSelecting, selectionBox, getCellsInfo, rectsIntersect, updateSelection, stopAutoScroll])

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button !== 0) return
    if (!containerRef.current?.contains(e.target as Node)) return
    startPointRef.current = { x: e.clientX, y: e.clientY }
    draggingRef.current = false
    if (!(e.shiftKey || e.ctrlKey || e.metaKey)) {
      const pos = detectClickedCell(e.clientX, e.clientY)
      if (pos) handleSingleClickSelection(pos)
    }
  }, [containerRef, detectClickedCell, handleSingleClickSelection])

  const handleKeyDownArrowSelection = useCallback((e: KeyboardEvent) => {
    if (!focusCell) return
    const shift = e.shiftKey
    let tempAnchor = anchorCell
    if (shift && !tempAnchor) tempAnchor = { ...focusCell }

    let newFocus = { ...focusCell }
    const maxRow = rows.length - 1
    const maxCol = colCount - 1

    if (e.key === 'ArrowUp') newFocus.rowIndex = Math.max(0, newFocus.rowIndex - 1)
    else if (e.key === 'ArrowDown') newFocus.rowIndex = Math.min(maxRow, newFocus.rowIndex + 1)
    else if (e.key === 'ArrowLeft') newFocus.colIndex = Math.max(0, newFocus.colIndex - 1)
    else if (e.key === 'ArrowRight') newFocus.colIndex = Math.min(maxCol, newFocus.colIndex + 1)

    const rowId = rows[newFocus.rowIndex]?.id
    const colField = table.getVisibleFlatColumns()[newFocus.colIndex]?.id

    setFocusCell(newFocus)

    if (shift && tempAnchor) {
      const newCells = getCellsInRange(tempAnchor, newFocus)
      setSelectedCells(prev => {
        if (!areSelectionsEqual(prev, newCells)) onSelectionChange?.(newCells)
        return newCells
      })
    } else {
      if (rowId != null && colField) {
        const single = [{ id: String(rowId), colField }]
        setSelectedCells(prev => {
          if (!areSelectionsEqual(prev, single)) onSelectionChange?.(single)
          return single
        })
      }
      tempAnchor = newFocus
    }

    setAnchorCell(tempAnchor)
    e.preventDefault()
  }, [anchorCell, colCount, focusCell, getCellsInRange, onSelectionChange, rows, table])

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown, { passive: false })
    document.addEventListener('mousemove', handleMouseMove, { passive: false })
    document.addEventListener('mouseup', finishDrag, { passive: false })

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', finishDrag)
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
      document.body.style.userSelect = ''
    }
  }, [handleMouseDown, handleMouseMove, finishDrag])

  return {
    selectionBox,
    selectedCells,
    setSelectedCells,
    anchorCell,
    focusCell,
    setAnchorCell,
    setFocusCell,
    handleKeyDownArrowSelection,
    edgeState,
    edgeIntensity,
    isSelecting,
  }
}
